import React, { FunctionComponent, useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { Modal } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';
import { propOr } from 'ramda';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import { Theme } from 'config';
import {
  AccountDetails,
  MerchantCustomerProfile,
} from 'scenes/AccountManagement/AccountManagement.helpers';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  merchantCustomerProfile: MerchantCustomerProfile;
  accountDetails: AccountDetails;
  setPaymentStatus: Function;
  buttonText: string;
};

const styles = (theme: Theme) => ({
  billingModal: {
    padding: '1.5625rem',
    height: '50rem',
    width: '100%',
  },
  embeddedWidget: {
    height: '100%',
    width: '100%',
    border: 'none',
  },
  missingText: {
    fontSize: '.85rem',
    color: `${theme.darkGray}`,
    margin: '0',
  },
  button: {
    backgroundColor: `${theme.violet}`,
    border: 'none',
    borderRadius: '1.5rem !important',
    color: '#fff !important',
    display: 'flex !important',
    fontSize: '.8rem',
    margin: '2.25rem 0 .25rem 0 !important',
    opacity: 1,
    padding: '0.5rem 4.25rem !important',
    fontWeight: '500 !important',
    lineHeight: '1.25rem !important',
    '&:hover': {
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  disabled: {
    backgroundColor: `${theme.disabledBtnGray}`,
    color: '#f7f8f9 !important',
    pointerEvents: 'none !important',
    marginBottom: '1rem !important',
  },
});

const encode = (string = '') => new Buffer(string).toString('base64');

const CFSModal: FunctionComponent<Props> = ({
  merchantCustomerProfile,
  accountDetails,
  buttonText,
  setPaymentStatus,
  classes,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const openModal = () => {
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
  };

  const customerId = propOr(
    null,
    'merchantCustomerId',
    merchantCustomerProfile,
  );
  const cfsPayload = {
    customerId,
    customer: {
      businessName: accountDetails.companyName,
      firstName: accountDetails.firstName,
      lastName: accountDetails.lastName,
      emailAddress: accountDetails.email,
      phoneNumber: accountDetails.phone.replace(/\D/g, ''),
    },
    address: {
      addressLine1: accountDetails.address1,
      addressLine2: accountDetails.address2,
      city: accountDetails.city,
      state: accountDetails.state,
      postal: accountDetails.postal,
    },
    requestToken: uuid(),
  };

  useEffect(() => {
    const cfsEvent = (event: any) => {
      if (event.data.cfsEvent === 'success') {
        setPaymentStatus('pending');
        closeModal();
      }
    };
    window.addEventListener('message', cfsEvent);

    return () => window.removeEventListener('message', cfsEvent);
  }, []);

  const isDisabled = !customerId;
  return (
    <>
      <button
        disabled={isDisabled}
        className={classnames(classes.button, {
          [classes.disabled]: isDisabled,
        })}
        onClick={openModal}
      >
        {buttonText}
      </button>
      {!customerId && (
        <p className={classes.missingText}>
          Customer Id is missing please contact support
        </p>
      )}
      <Modal closeIcon size="large" open={showModal} onClose={closeModal}>
        <Modal.Content className={classes.billingModal}>
          <iframe
            src={`${process.env.CFS_WIDGET}${encode(
              JSON.stringify(cfsPayload),
            )}`}
            title="CFS"
            className={classes.embeddedWidget}
          />
        </Modal.Content>
      </Modal>
    </>
  );
};

export default withStyles(styles)(CFSModal);
